import React from "react";
import { cn } from "@/lib/utils";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import {
  PlusIcon,
  VideoIcon,
  FileQuestionIcon,
  DownloadIcon,
} from "lucide-react";

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  onClick?: () => void;
}

interface AdminQuickActionsProps {
  /**
   * Custom actions
   */
  actions?: QuickAction[];

  /**
   * Create cohort callback
   */
  onCreateCohort?: () => void;

  /**
   * Add video callback
   */
  onAddVideo?: () => void;

  /**
   * Create quiz callback
   */
  onCreateQuiz?: () => void;

  /**
   * Export data callback
   */
  onExportData?: () => void;

  /**
   * Title
   */
  title?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export function AdminQuickActions({
  actions,
  onCreateCohort,
  onAddVideo,
  onCreateQuiz,
  onExportData,
  title = "Quick Actions",
  className,
}: AdminQuickActionsProps) {
  const defaultActions: QuickAction[] = [
    {
      id: "create-cohort",
      label: "Create Cohort",
      icon: <PlusIcon />,
      variant: "primary",
      onClick: onCreateCohort || (() => { window.location.href = "/admin/cohorts/new" }),
    },
    {
      id: "add-video",
      label: "Add Video",
      icon: <VideoIcon />,
      variant: "secondary",
      onClick: onAddVideo || (() => { window.location.href = "/admin/videos/new" }),
    },
    {
      id: "create-quiz",
      label: "Create Quiz",
      icon: <FileQuestionIcon />,
      variant: "secondary",
      onClick: onCreateQuiz || (() => { window.location.href = "/admin/quizzes/new" }),
    },
    {
      id: "export-data",
      label: "Export Data",
      icon: <DownloadIcon />,
      variant: "secondary",
      onClick: onExportData || (() => { window.location.href = "/admin/analytics" }),
    },
  ];

  const displayActions = actions || defaultActions;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">
          Common administrative tasks
        </p>
      </div>

      {/* Actions grid */}
      <div className="grid grid-cols-1 gap-3">
        {displayActions.map((action) => (
          <AcademicianStartupButton
            key={action.id}
            variant={action.variant || "secondary"}
            leftIcon={action.icon}
            onClick={action.onClick}
            className="w-full justify-start"
          >
            {action.label}
          </AcademicianStartupButton>
        ))}
      </div>
    </div>
  );
}
